import { toaster } from '@/components/ui/toaster';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  ApiResponse,
  BulkInvitationOutreachEmailRequest,
  BulkInvitationOutreachEmailResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

/**
 * Sends one email from the brand's connected mailbox to a selection of
 * invitations (explicit ids or the whole table filter) in a single call to
 * `POST /dashboard/affiliates/invitations/outreach-email`. The response counts
 * the queued emails and names each skipped invitation with its reason; the
 * caller renders that recap.
 *
 * Only a whole-request failure toasts here: a skipped invitation is not an
 * error, it belongs in the recap.
 *
 * The table selection is deliberately left alone: the composer dialog is opened
 * from the floating bar, so Chakra stacks it above the bar's layer, and closing
 * the bar here would dismiss the dialog before the recap shows. The table
 * clears it once the recap is closed.
 */
export function useSendBulkInvitationOutreachEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    BulkInvitationOutreachEmailResult,
    AxiosError<ApiErrorResponse>,
    BulkInvitationOutreachEmailRequest
  >({
    mutationFn: async (request): Promise<BulkInvitationOutreachEmailResult> => {
      const response = await api.post<ApiResponse<BulkInvitationOutreachEmailResult>>(
        '/dashboard/affiliates/invitations/outreach-email',
        request
      );

      if (!response.data.data) {
        throw new Error('Invalid response structure');
      }

      return response.data.data;
    },
    onSuccess: () => {
      // Renewed invitations change status and expiry, which the list and the
      // stats both read.
      queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.root });
    },
    onError: (error) => {
      // A plan refusal (a prospect whose address the plan withholds) opens the
      // upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      const errorCode = error.response?.data?.code;
      const fallback = error.response?.data?.message ?? t('invitationOutreach.send.error.generic');
      const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';

      toaster.create({
        title: t('invitationOutreach.send.error.title'),
        description: translated || fallback,
        type: 'error',
        duration: 6000,
      });
    },
  });
}
